import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBeratungSessions, BeratungSession } from "@/hooks/useBeratungSessions";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { 
  FileText, 
  Calendar, 
  Clock, 
  CheckCircle2, 
  PauseCircle, 
  XCircle, 
  PlayCircle,
  Users,
  Layers
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

interface BeratungHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentSessionId?: string;
  kundeId?: string;
  onSessionSelect?: (sessionId: string) => void;
}

const STATUS_CONFIG: Record<BeratungSession['status'], { label: string; icon: React.ReactNode; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  aktiv: { label: "Aktiv", icon: <PlayCircle className="h-3.5 w-3.5" />, variant: "default" },
  pausiert: { label: "Pausiert", icon: <PauseCircle className="h-3.5 w-3.5" />, variant: "secondary" },
  abgeschlossen: { label: "Abgeschlossen", icon: <CheckCircle2 className="h-3.5 w-3.5" />, variant: "outline" },
  abgebrochen: { label: "Abgebrochen", icon: <XCircle className="h-3.5 w-3.5" />, variant: "destructive" },
};

export function BeratungHistoryDialog({
  open,
  onOpenChange,
  currentSessionId,
  kundeId,
  onSessionSelect,
}: BeratungHistoryDialogProps) {
  const { data: sessions = [], isLoading } = useBeratungSessions({
    kundeId,
    enabled: open,
  });

  // Filter to only show completed sessions for history (excluding current active one)
  const historySessions = sessions.filter(s => 
    s.id !== currentSessionId && 
    ['abgeschlossen', 'pausiert', 'abgebrochen'].includes(s.status)
  );

  const formatDateTime = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "dd.MM.yyyy 'um' HH:mm", { locale: de });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (start: string, end: string | null) => {
    if (!end) return "Laufend";
    try {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const diffMs = endDate.getTime() - startDate.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 60) return `${diffMins} Min.`;
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      return `${hours} Std. ${mins} Min.`;
    } catch {
      return "—";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Beratungsverlauf
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[60vh] pr-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          ) : historySessions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground">
                Noch keine abgeschlossenen Beratungen vorhanden.
              </p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Beratungen werden hier angezeigt, sobald sie abgeschlossen wurden.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {historySessions.map(session => {
                const statusConfig = STATUS_CONFIG[session.status];
                
                return (
                  <button
                    key={session.id}
                    onClick={() => onSessionSelect?.(session.id)}
                    className="w-full text-left p-4 border rounded-lg hover:bg-muted/50 transition-colors group"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium truncate">
                            {session.name || "Beratung"}
                          </h4>
                          <Badge variant={statusConfig.variant} className="flex items-center gap-1">
                            {statusConfig.icon}
                            {statusConfig.label}
                          </Badge>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDateTime(session.gestartet_am)}
                          </span>
                          {session.beendet_am && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {formatDuration(session.gestartet_am, session.beendet_am)}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Layers className="h-3.5 w-3.5" />
                            {session.ausgewaehlte_module?.length || 0} Module
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            {session.teilnehmer?.length || 0} Teilnehmer
                          </span>
                        </div>
                        
                        {session.teilnehmer && session.teilnehmer.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {session.teilnehmer.slice(0, 3).map((t, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {t.name}
                              </Badge>
                            ))}
                            {session.teilnehmer.length > 3 && (
                              <Badge variant="secondary" className="text-xs">
                                +{session.teilnehmer.length - 3} weitere
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {session.protokoll_url && (
                        <Badge variant="outline" className="shrink-0">
                          <FileText className="h-3 w-3 mr-1" />
                          Protokoll
                        </Badge>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
